import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  LinearProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  IconButton,
  Paper,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  FormControl,
  Select,
  MenuItem,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
  Snackbar,
  Menu,
  ListItemIcon as MenuItemIcon,
  ListItemText as MenuItemText,
  Stack,
} from '@mui/material';
import {
  VerifiedUser,
  Warning,
  Schedule,
  CheckCircle,
  Cancel,
  TrendingUp,
  TrendingDown,
  Assessment,
  Security,
  Gavel,
  Flag,
  Person,
  Business,
  DirectionsCar,
  Description,
  CameraAlt,
  Fingerprint,
  Badge,
  AccountBalance,
  Download,
  Refresh,
  FilterList,
  MoreVert,
  Visibility,
  Edit,
  Note,
  Check,
  Close,
  DocumentScanner,
  Comment,
  History,
  AttachFile,
  Send,
  Print,
  Share,
  Delete,
  Block,
  Approve,
  Reject,
} from '@mui/icons-material';

import { VerificationStatus } from '@/types';
import { verificationService, type VerificationRecord, type VerificationListResponse, type AdminVerificationRequest } from '@/services/verificationService';
import { EnhancedVerificationItem } from '@/components/verification/EnhancedVerificationItem';
import VerificationDocumentViewer from '@/components/verification/VerificationDocumentViewer';
import { useNavigate } from 'react-router-dom';
import { logger } from '@/utils/logger';

const log = logger.module('VerificationOverviewPage');

interface VerificationMetric {
  category: string;
  total: number;
  verified: number;
  pending: number;
  rejected: number;
  draft: number;
  complianceRate: number;
  trend: number;
}

// Updated to work with backend verification records
interface ComplianceItem extends Omit<VerificationRecord, 'status'> {
  type: 'Identity' | 'Vehicle' | 'Insurance' | 'License' | 'Background';
  user: string;
  userType: 'Host' | 'Driver' | 'Renter';
  status: 'Verified' | 'Pending' | 'Rejected' | 'Expired' | 'Draft';
  submittedDate: string;
  reviewedDate?: string;
  expiryDate?: string;
  priority: 'High' | 'Medium' | 'Low';
  documents: string[];
  notes?: string;
  comments?: { author: string; text: string; date: string }[];
  attachments?: string[];
}

interface RegionalCompliance {
  region: string;
  totalUsers: number;
  verifiedUsers: number;
  complianceRate: number;
  requirements: {
    name: string;
    status: 'Compliant' | 'Non-Compliant' | 'Partial';
    details: string;
  }[];
}

export const VerificationOverviewPage: React.FC = () => {
  const navigate = useNavigate();
  const [selectedRegion, setSelectedRegion] = useState('All Regions');
  const [timeRange, setTimeRange] = useState('Last 30 Days');
  const [selectedItem, setSelectedItem] = useState<ComplianceItem | null>(null);
  const [viewDetailsOpen, setViewDetailsOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<'approve' | 'reject' | null>(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
  const [currentItemId, setCurrentItemId] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', type: 'success' as 'success' | 'error' });
  const [newComment, setNewComment] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // New search and filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending_review' | 'verified' | 'rejected'>('all');
  
  // Backend data state
  const [verifications, setVerifications] = useState<VerificationRecord[]>([]);
  const [rawVerificationData, setRawVerificationData] = useState<AdminVerificationRequest[]>([]);
  const [verificationStats, setVerificationStats] = useState<{
    total: number;
    pending: number;
    approved: number;
    rejected: number;
    draft: number;
    averageReviewTime: number;
  } | null>(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, hasMore: false });
  const [regionalCompliance, setRegionalCompliance] = useState<RegionalCompliance[]>([]);

  // Helper function to safely format dates
  const safeFormatDate = (dateString: string | undefined): string => {
    if (!dateString) return new Date().toISOString().split('T')[0];
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return new Date().toISOString().split('T')[0];
      }
      return date.toISOString().split('T')[0];
    } catch {
      return new Date().toISOString().split('T')[0];
    }
  };

  // Load data from backend on component mount
  useEffect(() => {
    loadVerificationData();
    loadVerificationStats();
    loadRegionalCompliance();

    // Set up auto-refresh interval to check for status changes
    const refreshInterval = setInterval(() => {
      loadVerificationData();
      loadRegionalCompliance();
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(refreshInterval);
  }, []);

  const loadVerificationData = async () => {
    setIsLoading(true);
    try {
      log.debug('Loading verification data', { statusFilter });
      // Use the new admin verification endpoint for basic user verifications
      const response = await verificationService.getAdminVerificationRequests({ status: statusFilter });
      log.debug('Verification data response', { success: response.success, dataLength: response.data?.data?.length });

      if (response.success && response.data && response.data.data) {

        // Store raw data for VerificationDocumentViewer
        setRawVerificationData(response.data.data);
        log.debug('Set raw verification data', { count: response.data.data.length });
        
        // Transform AdminVerificationRequest[] to VerificationRecord[] format
        const transformedVerifications: VerificationRecord[] = response.data.data.map(adminReq => ({
          id: adminReq.verificationId,
          userId: adminReq.userId,
          status: adminReq.status === 'pending_review' ? VerificationStatus.PENDING_REVIEW :
                  adminReq.status === 'verified' ? VerificationStatus.APPROVED :
                  adminReq.status === 'rejected' ? VerificationStatus.REJECTED :
                  VerificationStatus.PENDING_REVIEW,
          purpose: 'customer_verification',
          data: {
            personalInfo: {
              firstName: adminReq.userInfo.firstName,
              lastName: adminReq.userInfo.lastName,
              nin: '',
              dateOfBirth: '',
              gender: '',
              bvn: '',
              address: {
                street: '',
                city: '',
                state: '',
                postalCode: '',
                country: adminReq.countryCode
              }
            },
            driverLicense: {
              licenseNumber: '',
              licenseClass: '',
              issueDate: '',
              expiryDate: '',
              issuingState: '',
              frontImageUrl: adminReq.documentImageUrl,
              backImageUrl: adminReq.selfieImageUrl
            },
            vehicleInfo: { hasVehicle: false },
            guarantors: {
              primary: { name: '', relationship: '', phone: '', email: '', address: '' },
              secondary: { name: '', relationship: '', phone: '', email: '', address: '' }
            },
            backgroundCheck: {
              consentGiven: true,
              criminalHistoryDisclosure: '',
              drivingHistoryConsent: true
            },
            bankingInfo: {
              accountName: '',
              accountNumber: '',
              bankName: '',
              bankCode: ''
            }
          } as any,
          completionPercentage: adminReq.status === 'verified' ? 100 : 
                               adminReq.status === 'pending_review' ? 75 : 50,
          missingFields: adminReq.status === 'verified' ? [] : ['Additional verification needed'],
          submittedAt: adminReq.uploadedAt,
          reviewedAt: adminReq.reviewedAt,
          reviewerId: adminReq.reviewedBy,
          adminNotes: adminReq.notes,
          createdAt: adminReq.uploadedAt,
          updatedAt: adminReq.uploadedAt
        }));
        
        setVerifications(transformedVerifications);
        setPagination(prev => ({
          ...prev,
          total: response.data!.total,
          hasMore: false
        }));
      } else {
        // Fallback to mock data for demo purposes
        const mockVerifications = [{
          id: 'demo-1',
          userId: 'user-001',
          status: VerificationStatus.PENDING_REVIEW,
          purpose: 'customer_verification',
          data: {
            personalInfo: {
              firstName: 'John',
              lastName: 'Doe',
              nin: '12345678901',
              dateOfBirth: '1990-01-01',
              gender: 'male',
              bvn: '12345678901',
              address: {
                street: '123 Main St',
                city: 'Lagos',
                state: 'Lagos',
                postalCode: '100001',
                country: 'Nigeria'
              }
            },
            driverLicense: {
              licenseNumber: 'ABC123456',
              licenseClass: 'C',
              issueDate: '2020-01-01',
              expiryDate: '2025-01-01',
              issuingState: 'Lagos'
            },
            vehicleInfo: { hasVehicle: false },
            guarantors: {
              primary: { name: '', relationship: '', phone: '', email: '', address: '' },
              secondary: { name: '', relationship: '', phone: '', email: '', address: '' }
            },
            backgroundCheck: {
              consentGiven: true,
              criminalHistoryDisclosure: '',
              drivingHistoryConsent: true
            },
            bankingInfo: {
              accountName: '',
              accountNumber: '',
              bankName: '',
              bankCode: ''
            }
          } as any,
          completionPercentage: 75,
          missingFields: ['Banking Information'],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }];
        setVerifications(mockVerifications as any);
      }
    } catch (error) {
      console.error('Error loading verification data:', error);
      // Provide mock data on error
      setVerifications([]);
      setSnackbar({ open: true, message: 'API unavailable - showing interface only', type: 'warning' });
    } finally {
      setIsLoading(false);
    }
  };

  const loadVerificationStats = async () => {
    try {
      
      // Use existing verification data to avoid duplicate API calls
      if (verifications.length > 0) {
        const adminVerifications = verifications;
        
        // Calculate stats from admin verification data
        const calculatedStats = {
          total: adminVerifications.length,
          pending: adminVerifications.filter(v => 
            v.status === VerificationStatus.PENDING_REVIEW || 
            v.status === 'pending_review'
          ).length,
          approved: adminVerifications.filter(v => 
            v.status === VerificationStatus.APPROVED || 
            v.status === 'verified'
          ).length,
          rejected: adminVerifications.filter(v => 
            v.status === VerificationStatus.REJECTED || 
            v.status === 'rejected'
          ).length,
          draft: adminVerifications.filter(v => 
            v.status === VerificationStatus.DRAFT
          ).length,
          averageReviewTime: 18.5 // Would need historical data to calculate properly
        };
        
        setVerificationStats(calculatedStats);
      } else {
        // Provide fallback stats
        setVerificationStats({
          total: 0,
          pending: 0,
          approved: 0,
          rejected: 0,
          draft: 0,
          averageReviewTime: 0
        });
      }
    } catch (error) {
      console.error('❌ Error fetching verification stats:', error);
      
      // Provide fallback stats on error to prevent crashes
      const fallbackStats = {
        total: 0,
        pending: 0,
        approved: 0,
        rejected: 0,
        draft: 0,
        averageReviewTime: 0
      };
      
      setVerificationStats(fallbackStats);
      
      // Show user-friendly message about using demo data
      setSnackbar({ 
        open: true, 
        message: 'API unavailable - no verification data available', 
        type: 'warning' 
      });
    }
  };

  // Calculate verification metrics from backend stats
  const verificationMetrics: VerificationMetric[] = verificationStats ? [
    {
      category: 'Identity Verification',
      total: verificationStats.total,
      verified: verificationStats.approved,
      pending: verificationStats.pending,
      rejected: verificationStats.rejected,
      draft: verificationStats.draft,
      complianceRate: verificationStats.total > 0 ? Math.round((verificationStats.approved / verificationStats.total) * 100) : 0,
      trend: 2.4 // Would come from historical comparison
    },
    {
      category: 'Driver Applications',
      total: Math.round(verificationStats.total * 0.6), // Estimate driver applications
      verified: Math.round(verificationStats.approved * 0.6),
      pending: Math.round(verificationStats.pending * 0.7),
      rejected: Math.round(verificationStats.rejected * 0.5),
      draft: Math.round(verificationStats.draft * 0.4),
      complianceRate: verificationStats.total > 0 ? Math.round((verificationStats.approved * 0.6) / (verificationStats.total * 0.6) * 100) : 0,
      trend: -1.2
    },
    {
      category: 'Document Verification',
      total: Math.round(verificationStats.total * 1.3), // Multiple docs per user
      verified: Math.round(verificationStats.approved * 1.3),
      pending: Math.round(verificationStats.pending * 1.8),
      rejected: Math.round(verificationStats.rejected * 1.1),
      draft: Math.round(verificationStats.draft * 0.9),
      complianceRate: verificationStats.total > 0 ? Math.round((verificationStats.approved * 1.3) / (verificationStats.total * 1.3) * 100) : 0,
      trend: 3.8
    },
    {
      category: 'Background Checks',
      total: Math.round(verificationStats.total * 0.8),
      verified: Math.round(verificationStats.approved * 0.8),
      pending: Math.round(verificationStats.pending * 0.6),
      rejected: Math.round(verificationStats.rejected * 0.9),
      draft: Math.round(verificationStats.draft * 0.5),
      complianceRate: verificationStats.total > 0 ? Math.round((verificationStats.approved * 0.8) / (verificationStats.total * 0.8) * 100) : 0,
      trend: 1.9
    }
  ] : [];

  // Filter verifications by search query
  const filteredVerifications = verifications.filter(verification => {
    if (!searchQuery.trim()) return true;

    const searchLower = searchQuery.toLowerCase();
    const personalInfo = verification.data?.personalInfo;
    const fullName = personalInfo ? `${personalInfo.firstName} ${personalInfo.lastName}`.toLowerCase() : '';
    const userId = verification.userId.toLowerCase();
    const verificationId = verification.id.toLowerCase();

    return fullName.includes(searchLower) ||
           userId.includes(searchLower) ||
           verificationId.includes(searchLower);
  });

  // Transform backend verification records to compliance items
  const complianceItems: ComplianceItem[] = filteredVerifications.map((verification) => {
    const getTypeFromPurpose = (purpose: string): 'Identity' | 'Vehicle' | 'Insurance' | 'License' | 'Background' => {
      switch (purpose) {
        case 'driver_application': return 'License';
        case 'customer_verification': return 'Identity';
        default: return 'Identity';
      }
    };

    const getStatusLabel = (status: VerificationStatus): 'Verified' | 'Pending' | 'Rejected' | 'Expired' | 'Draft' => {
      switch (status) {
        case VerificationStatus.APPROVED: return 'Verified';
        case VerificationStatus.PENDING_REVIEW: return 'Pending';
        case VerificationStatus.REJECTED: return 'Rejected';
        case VerificationStatus.EXPIRED: return 'Expired';
        case VerificationStatus.DRAFT: return 'Draft';
        default: return 'Pending';
      }
    };

    const getPriorityFromCompletion = (completion: number): 'High' | 'Medium' | 'Low' => {
      if (completion < 50) return 'High';
      if (completion < 80) return 'Medium';
      return 'Low';
    };

    // Safely access verification data with null checks
    const personalInfo = verification.data?.personalInfo;
    const driverLicense = verification.data?.driverLicense;
    const vehicleInfo = verification.data?.vehicleInfo;
    const bankingInfo = verification.data?.bankingInfo;

    return {
      ...verification,
      type: getTypeFromPurpose(verification.purpose),
      user: personalInfo?.firstName ? 
        `${personalInfo.firstName} ${personalInfo.lastName}` : 
        `User ${verification.userId.slice(-4)}`,
      userType: verification.purpose === 'driver_application' ? 'Driver' : 'Renter',
      status: getStatusLabel(verification.status),
      submittedDate: verification.submittedAt || verification.createdAt,
      reviewedDate: verification.reviewedAt,
      priority: getPriorityFromCompletion(verification.completionPercentage),
      documents: [
        ...(driverLicense?.licenseNumber ? ['Driver License'] : []),
        ...(personalInfo?.nin ? ['National ID'] : []),
        ...(vehicleInfo?.hasVehicle ? ['Vehicle Documents'] : []),
        ...(bankingInfo?.accountNumber ? ['Banking Info'] : [])
      ],
      notes: verification.adminNotes || verification.rejectionReason,
      comments: [
        {
          author: 'System',
          text: 'Verification submitted for review',
          date: safeFormatDate(verification.createdAt)
        },
        ...(verification.reviewedAt ? [{
          author: 'Admin',
          text: verification.status === VerificationStatus.APPROVED ? 
            'Verification approved' : 
            verification.rejectionReason || 'Verification rejected',
          date: safeFormatDate(verification.reviewedAt)
        }] : [])
      ],
      attachments: [
        ...(driverLicense?.frontImageUrl ? ['license_front.jpg'] : []),
        ...(driverLicense?.backImageUrl ? ['license_back.jpg'] : []),
        ...(vehicleInfo?.registrationDocumentUrl ? ['vehicle_reg.pdf'] : [])
      ]
    };
  });

  const loadRegionalCompliance = async () => {
    try {
      const response = await verificationService.getRegionalCompliance(
        selectedRegion === 'All Regions' ? undefined : selectedRegion
      );

      if (response.success && response.data) {
        setRegionalCompliance(response.data.regions);
      } else {
        console.error('❌ Failed to load regional compliance:', response.error);
        // Keep existing data if load fails
        if (regionalCompliance.length === 0) {
          setSnackbar({
            open: true,
            message: 'Regional compliance data unavailable',
            type: 'warning'
          });
        }
      }
    } catch (error) {
      console.error('❌ Error loading regional compliance:', error);
      // Silent fail - keep existing data
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Verified': return 'success';
      case 'Pending': return 'warning';
      case 'Rejected': return 'error';
      case 'Expired': return 'error';
      case 'Draft': return 'info';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Verified': return <CheckCircle />;
      case 'Pending': return <Schedule />;
      case 'Rejected': return <Cancel />;
      case 'Expired': return <Warning />;
      case 'Draft': return <Edit />;
      default: return <Schedule />;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'Identity': return <Person />;
      case 'Vehicle': return <DirectionsCar />;
      case 'Insurance': return <Security />;
      case 'License': return <Badge />;
      case 'Background': return <Fingerprint />;
      default: return <Description />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'High': return 'error';
      case 'Medium': return 'warning';
      case 'Low': return 'success';
      default: return 'default';
    }
  };

  const getComplianceStatusColor = (status: string) => {
    switch (status) {
      case 'Compliant': return 'success';
      case 'Partial': return 'warning';
      case 'Non-Compliant': return 'error';
      default: return 'default';
    }
  };

  // Handler functions
  const handleViewDetails = (item: ComplianceItem) => {
    setSelectedItem(item);
    setViewDetailsOpen(true);
  };
  
  // Get the raw verification data for the selected item
  const getSelectedVerificationData = (): AdminVerificationRequest | undefined => {
    if (!selectedItem) return undefined;
    return rawVerificationData.find(v => v.verificationId === selectedItem.id);
  };

  const handleCloseDetails = () => {
    setViewDetailsOpen(false);
    setSelectedItem(null);
    setNewComment('');
  };

  const handleActionClick = (event: React.MouseEvent<HTMLElement>, itemId: string) => {
    setActionMenuAnchor(event.currentTarget);
    setCurrentItemId(itemId);
  };

  const handleActionClose = () => {
    setActionMenuAnchor(null);
    setCurrentItemId(null);
  };

  const handleConfirmAction = (action: 'approve' | 'reject') => {
    setConfirmAction(action);
    setConfirmDialogOpen(true);
    handleActionClose();
  };

  const handleConfirmClose = () => {
    setConfirmDialogOpen(false);
    setConfirmAction(null);
  };

  const handleActionConfirm = async () => {
    if (!confirmAction || !currentItemId) return;
    
    setIsLoading(true);
    
    try {
      if (confirmAction === 'approve') {
        const response = await verificationService.updateVerificationRequestStatus(
          currentItemId,
          'approve',
          'admin-123', // Would get from auth context
          newComment || 'Approved via admin panel'
        );
        if (response.success) {
          setSnackbar({ 
            open: true, 
            message: 'Verification approved successfully', 
            type: 'success' 
          });
          
          // Immediately update local state
          setVerifications(prev => prev.map(v => 
            v.id === currentItemId 
              ? { ...v, status: VerificationStatus.APPROVED, reviewedAt: new Date().toISOString() } 
              : v
          ));
          
          // Refresh data after a delay
          setTimeout(() => {
            loadVerificationData();
            loadVerificationStats();
          }, 1000);
        } else {
          throw new Error(response.error || 'Failed to approve verification');
        }
      } else {
        const response = await verificationService.updateVerificationRequestStatus(
          currentItemId,
          'reject',
          'admin-123', // Would get from auth context
          newComment || 'Rejected via admin panel'
        );
        if (response.success) {
          setSnackbar({ 
            open: true, 
            message: 'Verification rejected successfully', 
            type: 'success' 
          });
          
          // Immediately update local state
          setVerifications(prev => prev.map(v => 
            v.id === currentItemId 
              ? { 
                  ...v, 
                  status: VerificationStatus.REJECTED, 
                  rejectionReason: newComment || 'Rejected via admin panel',
                  reviewedAt: new Date().toISOString() 
                } 
              : v
          ));
          
          // Refresh data after a delay
          setTimeout(() => {
            loadVerificationData();
            loadVerificationStats();
          }, 1000);
        } else {
          throw new Error(response.error || 'Failed to reject verification');
        }
      }
    } catch (error) {
      console.error('Error processing verification action:', error);
      setSnackbar({ 
        open: true, 
        message: error instanceof Error ? error.message : 'Failed to process action', 
        type: 'error' 
      });
    } finally {
      setIsLoading(false);
      handleConfirmClose();
    }
  };

  const handleAddComment = () => {
    if (!newComment.trim() || !selectedItem) return;
    
    const updatedComments = [
      ...(selectedItem.comments || []),
      { author: 'Admin', text: newComment, date: new Date().toISOString().split('T')[0] }
    ];
    
    setSelectedItem({ ...selectedItem, comments: updatedComments });
    setNewComment('');
    setSnackbar({ open: true, message: 'Comment added successfully', type: 'success' });
  };

  const handleExportReport = () => {
    setSnackbar({ open: true, message: 'Report exported successfully', type: 'success' });
  };

  const handleRefreshData = async () => {
    setIsLoading(true);
    try {
      await Promise.all([
        loadVerificationData(),
        loadVerificationStats(),
        loadRegionalCompliance()
      ]);
      setSnackbar({ open: true, message: 'Data refreshed successfully', type: 'success' });
    } catch (error) {
      console.error('Error refreshing data:', error);
      setSnackbar({ open: true, message: 'Failed to refresh data', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBulkReview = () => {
    setSnackbar({ open: true, message: 'Bulk review feature coming soon', type: 'success' });
  };

  const handleFilter = () => {
    setSnackbar({ open: true, message: 'Filter feature coming soon', type: 'success' });
  };

  const handleViewFullReport = () => {
    setSnackbar({ open: true, message: 'Full compliance report feature coming soon', type: 'success' });
  };

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
            Verification & Compliance Overview
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Monitor verification status and regulatory compliance across regions
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
            >
              <MenuItem value="Last 7 Days">Last 7 Days</MenuItem>
              <MenuItem value="Last 30 Days">Last 30 Days</MenuItem>
              <MenuItem value="Last 90 Days">Last 90 Days</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<Download />}
            sx={{ textTransform: 'none' }}
            onClick={handleExportReport}
            data-testid="export-verification-report-btn"
          >
            Export Report
          </Button>
          <Button
            variant="contained"
            startIcon={<Refresh />}
            sx={{ textTransform: 'none' }}
            onClick={handleRefreshData}
            disabled={isLoading}
            data-testid="refresh-verification-data-btn"
          >
            {isLoading ? 'Refreshing...' : 'Refresh Data'}
          </Button>
        </Box>
      </Box>

      {/* Search and Filter Bar */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search by user name, email, or verification ID..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                <FilterList sx={{ color: 'text.secondary' }} />
              </Box>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <Select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as typeof statusFilter);
              // Trigger reload with new filter
              setTimeout(() => loadVerificationData(), 100);
            }}
            displayEmpty
          >
            <MenuItem value="all">All Statuses</MenuItem>
            <MenuItem value="pending_review">Pending Review</MenuItem>
            <MenuItem value="verified">Verified</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Verification Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {verificationMetrics.map((metric, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getTypeIcon(metric.category.split(' ')[0])}
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                      {metric.category}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {metric.trend > 0 ? (
                      <TrendingUp sx={{ color: 'success.main', fontSize: 16 }} />
                    ) : (
                      <TrendingDown sx={{ color: 'error.main', fontSize: 16 }} />
                    )}
                    <Typography variant="caption" color={metric.trend > 0 ? 'success.main' : 'error.main'}>
                      {metric.trend > 0 ? '+' : ''}{metric.trend}%
                    </Typography>
                  </Box>
                </Box>

                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                  {metric.complianceRate}%
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  Compliance Rate
                </Typography>

                <LinearProgress
                  variant="determinate"
                  value={metric.complianceRate}
                  sx={{ height: 6, borderRadius: 3, mb: 2 }}
                  color={metric.complianceRate > 85 ? 'success' : metric.complianceRate > 70 ? 'warning' : 'error'}
                />

                <Grid container spacing={1}>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">Verified</Typography>
                    <Typography variant="h6" color="success.main">{metric.verified}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">Pending</Typography>
                    <Typography variant="h6" color="warning.main">{metric.pending}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">Rejected</Typography>
                    <Typography variant="h6" color="error.main">{metric.rejected}</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Recent Compliance Items */}
        <Grid item xs={12} lg={8}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Recent Verification Submissions
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button variant="outlined" size="small" startIcon={<FilterList />} onClick={handleFilter}>
                    Filter
                  </Button>
                  <Button variant="outlined" size="small" startIcon={<Assessment />} onClick={handleBulkReview}>
                    Bulk Review
                  </Button>
                </Box>
              </Box>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Priority</TableCell>
                      <TableCell>Submitted</TableCell>
                      <TableCell>Documents</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(filteredVerifications || []).map((verification) => (
                      <EnhancedVerificationItem
                        key={verification.id}
                        verification={verification}
                        onViewDetails={(id) => {
                          const complianceItem = complianceItems.find(item => item.id === id);
                          if (complianceItem) {
                            handleViewDetails(complianceItem);
                          }
                        }}
                        onViewPhotos={(userId) => navigate(`/verification/photos/${userId}`)}
                        onApprove={(id) => handleConfirmAction('approve')}
                        onReject={(id) => handleConfirmAction('reject')}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Compliance Alerts */}
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Compliance Alerts
              </Typography>

              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  23 Driver Licenses Expiring in Next 7 Days
                </Typography>
                <Typography variant="body2">
                  Review and renew driver licenses to maintain platform compliance
                </Typography>
              </Alert>

              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  156 Vehicle Insurance Documents Pending Review
                </Typography>
                <Typography variant="body2">
                  High volume of pending insurance verifications may impact host onboarding
                </Typography>
              </Alert>

              <Alert severity="info">
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  New Regulatory Requirements - Ghana Commercial Vehicle License
                </Typography>
                <Typography variant="body2">
                  PSV license now required for commercial vehicle operations in Accra region
                </Typography>
              </Alert>
            </CardContent>
          </Card>
        </Grid>

        {/* Regional Compliance */}
        <Grid item xs={12} lg={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Regional Compliance
                </Typography>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Select
                    value={selectedRegion}
                    onChange={(e) => setSelectedRegion(e.target.value)}
                  >
                    <MenuItem value="All Regions">All Regions</MenuItem>
                    <MenuItem value="Lagos">Lagos</MenuItem>
                    <MenuItem value="Nairobi">Nairobi</MenuItem>
                    <MenuItem value="Accra">Accra</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {regionalCompliance.map((region, index) => (
                <Card key={index} sx={{ mb: 3, backgroundColor: 'grey.50' }}>
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {region.region}
                      </Typography>
                      <Chip
                        label={`${region.complianceRate}%`}
                        size="small"
                        color={region.complianceRate > 85 ? 'success' : 'warning'}
                      />
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        {region.verifiedUsers} of {region.totalUsers} users verified
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={region.totalUsers > 0 ? (region.verifiedUsers / region.totalUsers) * 100 : 0}
                        sx={{ height: 4, borderRadius: 2, mt: 0.5 }}
                        color={region.complianceRate > 85 ? 'success' : 'warning'}
                      />
                    </Box>

                    <Typography variant="caption" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
                      Regulatory Requirements:
                    </Typography>
                    
                    {region.requirements.map((req, reqIndex) => (
                      <Box key={reqIndex} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          {req.name}
                        </Typography>
                        <Chip
                          label={req.status}
                          size="small"
                          color={getComplianceStatusColor(req.status) as any}
                          variant="outlined"
                          sx={{ fontSize: '0.6rem', height: 20 }}
                        />
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              ))}

              <Button variant="outlined" fullWidth size="small" sx={{ textTransform: 'none' }} onClick={handleViewFullReport} data-testid="view-full-compliance-report-btn">
                View Full Compliance Report
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleActionClose}
      >
        <MenuItem onClick={() => handleConfirmAction('approve')} data-testid="approve-verification-btn">
          <MenuItemIcon>
            <CheckCircle sx={{ color: 'success.main' }} />
          </MenuItemIcon>
          <MenuItemText>Approve</MenuItemText>
        </MenuItem>
        <MenuItem onClick={() => handleConfirmAction('reject')} data-testid="reject-verification-btn">
          <MenuItemIcon>
            <Cancel sx={{ color: 'error.main' }} />
          </MenuItemIcon>
          <MenuItemText>Reject</MenuItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleActionClose}>
          <MenuItemIcon>
            <History />
          </MenuItemIcon>
          <MenuItemText>View History</MenuItemText>
        </MenuItem>
        <MenuItem onClick={handleActionClose}>
          <MenuItemIcon>
            <Print />
          </MenuItemIcon>
          <MenuItemText>Print Document</MenuItemText>
        </MenuItem>
      </Menu>

      {/* View Details Dialog - Using VerificationDocumentViewer */}
      {getSelectedVerificationData() && (
        <VerificationDocumentViewer
          open={viewDetailsOpen}
          onClose={handleCloseDetails}
          verificationRequest={getSelectedVerificationData()!}
          onApprove={async (verificationId, notes) => {
            try {
              log.info('Starting approval from VerificationOverviewPage', { verificationId });
              setIsLoading(true);

              const result = await verificationService.approveVerification(verificationId, notes);
              log.debug('Approval result received', { verificationId, success: result.success, result });

              if (result.success) {
                log.info('Approval successful, closing dialog and refreshing', { verificationId });
                setSnackbar({ open: true, message: 'Verification approved successfully!', type: 'success' });
                handleCloseDetails();
                // Refresh the data to show updated status
                log.debug('Calling loadVerificationData', { verificationId });
                await loadVerificationData();
                log.info('Data refresh complete', { verificationId });
              } else {
                throw new Error(result.error || 'Failed to approve verification');
              }
            } catch (error) {
              log.error('Error approving verification', { verificationId, error });
              setSnackbar({
                open: true,
                message: `Failed to approve verification: ${error instanceof Error ? error.message : 'Unknown error'}`,
                type: 'error'
              });
            } finally {
              setIsLoading(false);
            }
          }}
          onReject={async (verificationId, notes, rejectedFields) => {
            try {
              setIsLoading(true);
              
              const rejectionReason = notes || 'No reason provided';
              const result = await verificationService.rejectVerification(
                verificationId, 
                rejectionReason, 
                notes,
                rejectedFields
              );
              
              if (result.success) {
                setSnackbar({ open: true, message: 'Verification rejected successfully!', type: 'success' });
                
                // Immediately update the local state to reflect the change
                setVerifications(prev => prev.map(v => 
                  v.id === verificationId 
                    ? { 
                        ...v, 
                        status: VerificationStatus.REJECTED,
                        rejectionReason,
                        adminNotes: notes,
                        rejectedFields,
                        reviewedAt: new Date().toISOString()
                      } 
                    : v
                ));
                
                // Update raw verification data as well
                setRawVerificationData(prev => prev.map(v => 
                  v.verificationId === verificationId 
                    ? { 
                        ...v, 
                        status: 'rejected',
                        notes,
                        reviewedAt: new Date().toISOString()
                      } 
                    : v
                ));
                
                // Refresh the data from backend to ensure consistency
                setTimeout(() => {
                  loadVerificationData();
                  loadVerificationStats();
                }, 1000);
                
                handleCloseDetails();
              } else {
                throw new Error(result.error || 'Failed to reject verification');
              }
            } catch (error) {
              console.error('❌ Error rejecting verification:', error);
              setSnackbar({ 
                open: true, 
                message: `Failed to reject verification: ${error instanceof Error ? error.message : 'Unknown error'}`, 
                type: 'error' 
              });
            } finally {
              setIsLoading(false);
            }
          }}
        />
      )}
      
      {/* Old Dialog (keeping temporarily for reference) */}
      {false && (
      <Dialog
        open={viewDetailsOpen}
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {selectedItem && getTypeIcon(selectedItem.type)}
            <Typography variant="h6">
              {selectedItem?.type} Verification - {selectedItem?.user}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedItem && (
            <Box>
              {/* Status and Priority */}
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <Chip
                  icon={getStatusIcon(selectedItem.status)}
                  label={selectedItem.status}
                  color={getStatusColor(selectedItem.status) as any}
                />
                <Chip
                  label={selectedItem.priority}
                  color={getPriorityColor(selectedItem.priority) as any}
                  variant="outlined"
                />
                <Chip
                  label={selectedItem.userType}
                  variant="outlined"
                />
              </Box>

              {/* Documents */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Documents
                </Typography>
                <Grid container spacing={1}>
                  {(selectedItem.documents || []).map((doc, index) => (
                    <Grid item key={index}>
                      <Chip
                        icon={<DocumentScanner />}
                        label={doc}
                        variant="outlined"
                        size="small"
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>

              {/* Attachments */}
              {selectedItem.attachments && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Attachments
                  </Typography>
                  <Grid container spacing={1}>
                    {(selectedItem.attachments || []).map((file, index) => (
                      <Grid item key={index}>
                        <Chip
                          icon={<AttachFile />}
                          label={file}
                          variant="outlined"
                          size="small"
                          onClick={() => setSnackbar({ open: true, message: `Opening ${file}`, type: 'success' })}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* Notes */}
              {selectedItem.notes && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Review Notes
                  </Typography>
                  <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
                    <Typography variant="body2">
                      {selectedItem.notes}
                    </Typography>
                  </Paper>
                </Box>
              )}

              {/* Comments */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Comments
                </Typography>
                <Box sx={{ maxHeight: 200, overflowY: 'auto', mb: 2 }}>
                  {selectedItem.comments?.map((comment, index) => (
                    <Paper key={index} sx={{ p: 2, mb: 1, backgroundColor: 'grey.50' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {comment.author}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {comment.date}
                        </Typography>
                      </Box>
                      <Typography variant="body2">
                        {comment.text}
                      </Typography>
                    </Paper>
                  ))}
                </Box>
                
                {/* Add Comment */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Add a comment..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    multiline
                    rows={2}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleAddComment}
                    disabled={!newComment.trim()}
                    sx={{ minWidth: 'auto' }}
                  >
                    <Send />
                  </Button>
                </Box>
              </Box>

              {/* Timeline */}
              <Box>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Timeline
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <Schedule />
                    </ListItemIcon>
                    <ListItemText
                      primary="Submitted"
                      secondary={new Date(selectedItem.submittedDate).toLocaleString()}
                    />
                  </ListItem>
                  {selectedItem.reviewedDate && (
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle />
                      </ListItemIcon>
                      <ListItemText
                        primary="Reviewed"
                        secondary={new Date(selectedItem.reviewedDate).toLocaleString()}
                      />
                    </ListItem>
                  )}
                  {selectedItem.expiryDate && (
                    <ListItem>
                      <ListItemIcon>
                        <Warning />
                      </ListItemIcon>
                      <ListItemText
                        primary="Expires"
                        secondary={new Date(selectedItem.expiryDate).toLocaleString()}
                      />
                    </ListItem>
                  )}
                </List>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetails}>Close</Button>
          <Button variant="outlined" startIcon={<Print />} onClick={() => setSnackbar({ open: true, message: 'Print functionality coming soon', type: 'success' })}>
            Print
          </Button>
          <Button variant="outlined" startIcon={<Share />} onClick={() => setSnackbar({ open: true, message: 'Share functionality coming soon', type: 'success' })}>
            Share
          </Button>
        </DialogActions>
      </Dialog>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onClose={handleConfirmClose}>
        <DialogTitle>
          Confirm {confirmAction === 'approve' ? 'Approval' : 'Rejection'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to {confirmAction} this verification request?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmClose}>Cancel</Button>
          <Button
            onClick={handleActionConfirm}
            variant="contained"
            color={confirmAction === 'approve' ? 'success' : 'error'}
            disabled={isLoading}
          >
            {isLoading ? 'Processing...' : `${confirmAction === 'approve' ? 'Approve' : 'Reject'}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Box>
  );
};